/**
 * MaintenanceReportPage — V2 main orchestrator for the Maintenance Report.
 *
 * Unified page for both cloud and on-premise campuses.
 * Detects `is_cloud` from session campusConfig to switch between:
 *   - Cloud: `is_cloud: true` in API payload, cloud column set
 *   - On-premise: `campus_guid` in API payload, on-premise column set
 *
 * Simplified compared to AlertDetailsPage: no summary view, no event notes,
 * no reason editing. Only the detailed table with CSV/PDF export.
 *
 * Replaces V1 MaintenanceReports.jsx (~1227 lines) and
 * MaintenanceCloudReports.jsx (~1008 lines).
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import Pagination from '../../../components/v2/Pagination.jsx';
import Toast from '../../../components/v2/Toast.jsx';
import useToast from '../../../hooks/useToast.js';
import {
    getSession,
    getCampusId,
    getUserId,
    getSessionId,
    getCampusList,
    getCampusGuid,
    updateSessionCampus,
    getUsername,
    getCampusConfig,
    getCampusTzOffset,
} from '../../../services/session.js';
import { iAmHigherAuthority, formatDateTimeIso, formatDateTime12Hour, getToday } from '../../../libs.jsx';
import { exportMaintenanceCsv, exportMaintenancePdf } from '../../../services/csvExport.js';
import {
    fetchMaintenanceReport,
    fetchFilterOptions,
} from './api/maintenanceApi.js';

import MaintenanceTable, { COLUMN_DEFS_CLOUD, COLUMN_DEFS_ONPREMISE } from './components/MaintenanceTable.jsx';
import ReportFilterDrawer from '../alert-details/components/ReportFilterDrawer.jsx';
import ScheduleReportDrawer from '../alert-details/components/ScheduleReportDrawer.jsx';
import { fetchFrequencies, createSchedule } from '../scheduled/api/scheduledApi.js';
import ColumnVisibilityToggle from '../../../components/v2/ColumnVisibilityToggle.jsx';
import usePersistedColumns from '../../../hooks/usePersistedColumns.js';
import usePersistedPerPage from '../../../hooks/usePersistedPerPage.js';

import '../alert-details/alert-details-page.css';
import './maintenance-report-page.css';
import { compareValues } from '../../../utils/sortRows.js';
import { expandReasonIds } from '../../../utils/reasonFilterIds.js';
import { REPORT_TYPE_ID } from '../../../utils/reportTypes.js';
import { buildFilterChips, countActiveFilters } from '../../../utils/reportFilterChips.js';
import { MAINTENANCE_REPORT_SECTIONS } from '../../../components/v2/reportFilterSections.js';
import { reportErrorMessage, clearCampusFilters } from '../../../utils/reportPageState.js';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Maintenance always runs for "right now" — single Today range */
const TODAY_RANGE = { id: 1, label: 'Today', getRange: () => { const d = getToday(); d.setHours(0,0,0,0); const e = new Date(); return [d, e]; } };

/* Filter drawer sections: MAINTENANCE_REPORT_SECTIONS (components/v2/reportFilterSections.js),
   Event Types only — no Reasons, no Response Time. */


function emptyReportFilters() {
    return {
        areas: [],
        areaGroups: [],
        areaTypes: [],
        residents: [],
        residentGroups: [],
        users: [],
        userGroups: [],
        deviceCategories: [],
        devices: [],
        eventTypes: [],
        reasons: [],
    };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const MaintenanceReportPage = () => {
    // -- Session --
    const session = useMemo(() => getSession(), []);
    const campusList = useMemo(() => getCampusList() || [], []);
    const [campusId, setCampusId] = useState(() => getCampusId());
    const campusConfig = useMemo(() => getCampusConfig(campusId), [campusId]);

    const isCloud = Boolean(campusConfig?.is_cloud);
    const tzOffset = getCampusTzOffset(campusId);
    const columnDefs = isCloud ? COLUMN_DEFS_CLOUD : COLUMN_DEFS_ONPREMISE;

    // -- Navigation state (from ScheduledReportsPage "Run Report Local") --
    const location = useLocation();
    const navStateRef = useRef(location.state);
    const navState = navStateRef.current;

    // -- Report state --
    const [status, setStatus] = useState('init'); // 'init' | 'loading' | 'ready'
    const [reportData, setReportData] = useState([]);
    const [overLimit, setOverLimit] = useState(false);
    const [csvUrl, setCsvUrl] = useState('');

    // -- Time range (always Today for maintenance) --
    const [rangeId] = useState(1);
    const [rangeName] = useState('Today');
    const [customRange] = useState(false);
    const [start, setStart] = useState(() => { const d = getToday(); d.setHours(0,0,0,0); return d; });
    const [end, setEnd] = useState(() => new Date());
    const [startDisplay, setStartDisplay] = useState('');
    const [endDisplay, setEndDisplay] = useState('');

    // -- Report filters (pre-query) — pre-fill from navigation state if
    //    present, but only the array-shaped keys this page knows about.
    //    parseFilterTerms() returns extra non-array keys (e.g. responseTime)
    //    that would break the filter drawer. --
    const [reportFilters, setReportFilters] = useState(() => {
        const base = emptyReportFilters();
        if (!navState?.filters) return base;
        const allowed = {};
        Object.keys(base).forEach((k) => {
            if (Array.isArray(navState.filters[k])) allowed[k] = navState.filters[k];
        });
        return { ...base, ...allowed };
    });
    const [filterOptions, setFilterOptions] = useState({});
    // True once the filter-options fetch has finished, whether or not it succeeded.
    const [optionsSettled, setOptionsSettled] = useState(false);
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

    // -- Schedule drawer --
    const [scheduleDrawerOpen, setScheduleDrawerOpen] = useState(false);
    const [frequencies, setFrequencies] = useState([]);

    // -- Table state --
    const [search, setSearch] = useState('');
    const [columnFilters, setColumnFilters] = useState({});
    const [sortColumn, setSortColumn] = useState(() => isCloud ? 'event_datetime_utc' : 'EventDateTime');
    const [sortOrder, setSortOrder] = useState('asc');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = usePersistedPerPage();
    const allColumnKeys = useMemo(() => columnDefs.map((c) => c.key), [columnDefs]);
    const [visibleColumns, handleToggleColumn] = usePersistedColumns('report-maintenance', allColumnKeys, allColumnKeys);

    // -- UI --
    const shouldRerunRef = useRef(false);
    const pendingAutoRunRef = useRef(false);

    const { toast, showToast: showTypedToast, dismissToast } = useToast();
    /* This page's calls are (message, type); the shared hook is (type, message). */
    const showToast = useCallback((message, type = 'success') => showTypedToast(type, message), [showTypedToast]);

    // ======================================================================
    // Reset table state when campus changes cloud/on-prem mode
    // ======================================================================
    const prevIsCloudRef = useRef(isCloud);
    useEffect(() => {
        if (prevIsCloudRef.current !== isCloud) {
            prevIsCloudRef.current = isCloud;
            setSortColumn(isCloud ? 'event_datetime_utc' : 'EventDateTime');
            setSortOrder('asc');
            setColumnFilters({});
            setSearch('');
            setPage(1);
        }
    }, [isCloud]);

    // ======================================================================
    // Load filter options on mount / campus change
    // ======================================================================
    useEffect(() => {
        if (!campusId) return;
        setOptionsSettled(false);
        fetchFilterOptions(campusId)
            .then((opts) => { setFilterOptions(opts); setOptionsSettled(true); })
            .catch(() => setOptionsSettled(true));
        fetchFrequencies().then((d) => setFrequencies(Array.isArray(d) ? d : []));
    }, [campusId]);

    // ======================================================================
    // Time range helpers — always refresh start/end to "now" before run
    // ======================================================================

    // ======================================================================
    // Run Report
    // ======================================================================
    const handleRunReport = useCallback(async () => {
        setStatus('loading');
        setOverLimit(false);

        // Always run for Today (now)
        const todayStart = getToday();
        todayStart.setHours(0,0,0,0);
        const now = new Date();
        setStart(todayStart);
        setEnd(now);

        const stD = formatDateTimeIso(todayStart);
        const enD = formatDateTimeIso(now);

        setStartDisplay(formatDateTime12Hour(todayStart));
        setEndDisplay(formatDateTime12Hour(now));

        // Build filter strings from arrays. Drawer keys reasons by
        // cloud_reason_code_id; combine with each row's matching reason_code_id.
        const combinedReasonIds = expandReasonIds(reportFilters.reasons, filterOptions.reasonCodes);
        const filterPayload = {
            areas: (reportFilters.areas || []).join(','),
            areaGroups: (reportFilters.areaGroups || []).join(','),
            areaTypes: (reportFilters.areaTypes || []).join(','),
            residents: (reportFilters.residents || []).join(','),
            residentGroups: (reportFilters.residentGroups || []).join(','),
            users: (reportFilters.users || []).join(','),
            userGroups: (reportFilters.userGroups || []).join(','),
            deviceCategories: (reportFilters.deviceCategories || []).join(','),
            devices: (reportFilters.devices || []).join(','),
            eventTypes: (reportFilters.eventTypes || []).join(','),
            reasons: combinedReasonIds.join(','),
        };

        const result = await fetchMaintenanceReport({
            campusId,
            campusGuid: getCampusGuid(campusId),
            isCloud,
            username: getUsername(),
            startDate: stD,
            endDate: enD,
            filters: filterPayload,
        });

        if (result.ErrorCode) {
            showToast(reportErrorMessage(result.ErrorCode), 'error');
            setStatus('init');
            return;
        }

        if (result.overlimit) {
            setOverLimit(true);
            setCsvUrl(result.url || '');
            showToast('Report exceeds 5,000 events. Details will need to be downloaded.', 'warning');
        } else {
            setOverLimit(false);
            setCsvUrl(result.url || '');
        }

        setReportData(result.records || []);
        setPage(1);
        setStatus('ready');
    }, [filterOptions, campusId, isCloud, reportFilters, showToast]);

    // ======================================================================
    // Re-run report after filter apply / clear (if already run)
    // ======================================================================
    useEffect(() => {
        if (shouldRerunRef.current) {
            shouldRerunRef.current = false;
            handleRunReport();
        }
    }, [reportFilters, handleRunReport]);

    // Auto-run on mount when arriving from ScheduledReportsPage "Run Report Local"
    const autoRanRef = useRef(false);
    useEffect(() => {
        if (autoRanRef.current) return;
        if (!navState?.autoRun) return;
        /* Held until the filter options have settled when a reason filter is set:
           the request expands reasons from those options (utils/reasonFilterIds.js),
           and firing first would send cloud ids only and return fewer rows. */
        if ((reportFilters.reasons || []).length > 0 && !optionsSettled) return;
        autoRanRef.current = true;
        handleRunReport();
    }, [navState, handleRunReport, optionsSettled, reportFilters]);

    // Auto-run after filter apply (if already run)
    useEffect(() => {
        if (pendingAutoRunRef.current) {
            pendingAutoRunRef.current = false;
            handleRunReport();
        }
    });

    // ======================================================================
    // Data pipeline: search → column filters → sort → paginate
    // ======================================================================
    const searchedData = useMemo(() => {
        if (!search) return reportData;
        const term = search.toLowerCase();
        if (isCloud) {
            return reportData.filter((row) =>
                (row.condition || '').toLowerCase().includes(term) ||
                (row.device_name || '').toLowerCase().includes(term) ||
                (row.manufacture_uuid || '').toLowerCase().includes(term) ||
                (row.area_name || '').toLowerCase().includes(term) ||
                (row.resident_name || '').toLowerCase().includes(term)
            );
        }
        return reportData.filter((row) =>
            (row.AlertSubType || '').toLowerCase().includes(term) ||
            (row.DeviceUse || '').toLowerCase().includes(term) ||
            (row.SerialNumber || '').toLowerCase().includes(term) ||
            (row.LocationName || '').toLowerCase().includes(term) ||
            (row.LocationArea || '').toLowerCase().includes(term) ||
            (row.Residents || '').toLowerCase().includes(term)
        );
    }, [reportData, search, isCloud]);

    const columnFilteredData = useMemo(() => {
        const activeFilters = Object.entries(columnFilters).filter(([, vals]) => vals.length > 0);
        if (activeFilters.length === 0) return searchedData;
        return searchedData.filter((row) =>
            activeFilters.every(([key, vals]) => {
                const val = row[key] != null ? String(row[key]) : '';
                return vals.includes(val);
            })
        );
    }, [searchedData, columnFilters]);

    const sortedData = useMemo(() => {
        if (!sortColumn) return columnFilteredData;
        const sorted = [...columnFilteredData].sort((a, b) => {
            // Handle virtual columns
            if (sortColumn === '_duration') {
                const dateKey = isCloud ? 'event_datetime_utc' : 'EventDateTime';
                const aTime = a[dateKey] ? new Date(a[dateKey]).getTime() : 0;
                const bTime = b[dateKey] ? new Date(b[dateKey]).getTime() : 0;
                // Longer duration = earlier datetime, so sort by datetime
                return sortOrder === 'asc' ? aTime - bTime : bTime - aTime;
            }

            // Blank values sort last in both directions (utils/sortRows.js).
            return compareValues(a[sortColumn], b[sortColumn], sortOrder);
        });
        return sorted;
    }, [columnFilteredData, sortColumn, sortOrder, isCloud]);

    const totalFiltered = sortedData.length;
    const totalPages = Math.max(1, Math.ceil(totalFiltered / perPage));
    const safePage = Math.min(page, totalPages);
    const paginatedData = useMemo(() => {
        const startIdx = (safePage - 1) * perPage;
        return sortedData.slice(startIdx, startIdx + perPage);
    }, [sortedData, safePage, perPage]);

    // ======================================================================
    // Handlers
    // ======================================================================
    const handleSort = useCallback((colKey) => {
        setSortColumn((prev) => {
            if (prev === colKey) {
                setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
                return prev;
            }
            setSortOrder('asc');
            return colKey;
        });
        setPage(1);
    }, []);

    const handleColumnFilterChange = useCallback((columnKey, selectedValues) => {
        setColumnFilters((prev) => ({ ...prev, [columnKey]: selectedValues }));
        setPage(1);
    }, []);

    const handleClearColumnFilters = useCallback(() => {
        setColumnFilters({});
        setPage(1);
    }, []);

    const handleSearchChange = useCallback((e) => {
        setSearch(e.target.value);
        setPage(1);
    }, []);

    const handleCampusChange = useCallback((e) => {
        const newId = parseInt(e.target.value, 10);
        setCampusId(newId);
        updateSessionCampus(newId);
        /* The filter lists hold the old campus's ids, which would still be sent (A21). */
        setReportFilters(clearCampusFilters);
        setReportData([]);
        setStatus('init');
    }, []);

    // -- Filter drawer --
    const handleApplyFilters = useCallback((filters) => {
        // Strip responseTime if present (maintenance doesn't use it)
        const { responseTime, ...maintenanceFilters } = filters;
        setReportFilters(maintenanceFilters);
        setFilterDrawerOpen(false);
        if (status !== 'init') shouldRerunRef.current = true;
    }, [status]);

    const handleClearReportFilters = useCallback(() => {
        setReportFilters(emptyReportFilters());
        if (status !== 'init') shouldRerunRef.current = true;
    }, [status]);

    // Count active report-level filters
    /* Active filters, not selected items — the same badge on every report (audit A22). */
    const activeReportFilterCount = useMemo(() => countActiveFilters(reportFilters, MAINTENANCE_REPORT_SECTIONS), [reportFilters]);

    // -- Export --
    const handleExportCsv = useCallback(() => {
        exportMaintenanceCsv(sortedData, isCloud, 'maintenance-report.csv', visibleColumns);
    }, [sortedData, isCloud, visibleColumns]);

    const handleExportPdf = useCallback(() => {
        exportMaintenancePdf(sortedData, isCloud, 'Maintenance Report', `${startDisplay} - ${endDisplay}`, 'maintenance-report.pdf', visibleColumns);
    }, [sortedData, isCloud, startDisplay, endDisplay, visibleColumns]);

    // Schedule
    const handleScheduleSave = useCallback(async (payload) => {
        const result = await createSchedule(payload, { campusId, userId: getUserId(), clientId: getSessionId() });
        if (result[0]) { showToast('Schedule created successfully.'); setScheduleDrawerOpen(false); }
        else { showToast(result[1] || 'Failed to create schedule.', 'error'); }
    }, [campusId, showToast]);

    // ======================================================================
    // Render helpers
    // ======================================================================
    /* utils/reportFilterChips.js — names resolved per sub-section idKey (audit B16). */
    const reportFilterChips = useMemo(() => buildFilterChips(reportFilters, filterOptions, MAINTENANCE_REPORT_SECTIONS), [reportFilters, filterOptions]);

    // ======================================================================
    // Render
    // ======================================================================
    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
                <div className="ps-page">

                    {/* Toast */}
                    <Toast toast={toast} onDismiss={dismissToast} />

                    {/* PAGE HEADER */}
                    <div className="ps-page-header">
                        <div className="ps-page-header__inner">
                            {/* Breadcrumb + Campus Selector */}
                            <div className="ps-breadcrumb-row">
                                <div className="ps-breadcrumb">
                                    <span className="ps-breadcrumb__text">Reports</span>
                                    <svg className="ar-icon-md ps-breadcrumb__separator" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                    </svg>
                                    <span className="ps-breadcrumb__current">Maintenance</span>
                                </div>

                                {/* Campus selector */}
                                <div className="ps-campus-row">
                                    <label htmlFor="mr-campus-selector" className="ps-campus-label">Campus:</label>
                                    {campusList.length > 1 ? (
                                        <select
                                            id="mr-campus-selector"
                                            value={campusId}
                                            onChange={handleCampusChange}
                                            className="ps-campus-select"
                                        >
                                            {campusList.map((c) => (
                                                <option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className="ps-campus-name">{campusConfig?.name || ''}</span>
                                    )}
                                </div>
                            </div>

                            {/* Page title + static Today badge + actions row */}
                            <div className="ar-title-row">
                                <div className="ar-title-row__left">
                                    <h1 className="ps-page-title">Maintenance</h1>
                                </div>
                                <div className="ar-title-row__actions">
                                    <span className="ar-time-summary" style={{ cursor: 'default' }}>
                                        <i className="fe fe-clock" style={{ marginRight: '4px', fontSize: 'var(--font-size-xs)' }} />
                                        Today
                                    </span>
                                    <button className="dp-btn dp-btn--secondary dp-btn--sm" onClick={() => setScheduleDrawerOpen(true)}>
                                        <i className="fe fe-calendar" style={{ marginRight: '4px' }} />Schedule Report
                                    </button>
                                    {!isCloud ? (
                                        <button
                                            className="dp-btn dp-btn--secondary dp-btn--sm"
                                            onClick={() => setFilterDrawerOpen(true)}
                                            style={{ position: 'relative' }}
                                        >
                                            <i className="fe fe-sliders" style={{ marginRight: '4px' }} />
                                            Report Settings
                                            {activeReportFilterCount > 0 ? (
                                                <span className="ar-filter-badge">{activeReportFilterCount}</span>
                                            ) : null}
                                        </button>
                                    ) : null}
                                    <button
                                        className="dp-btn dp-btn--primary dp-btn--sm"
                                        onClick={handleRunReport}
                                        disabled={status === 'loading'}
                                    >
                                        <i className="fe fe-refresh-cw" style={{ marginRight: '4px' }} />
                                        {status === 'loading' ? 'Running...' : 'Run Report'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* MAIN CONTENT */}
                    <div className="ar-content">

                    {/* Report filter chips */}
                    {reportFilterChips.length > 0 ? (
                        <div className="ar-filter-chips">
                            {reportFilterChips.map((chip) => (
                                <span key={chip.key} className="ar-filter-chip">{chip.label}</span>
                            ))}
                            <button
                                className="ar-filter-chips__clear"
                                onClick={handleClearReportFilters}
                            >
                                Clear All
                            </button>
                        </div>
                    ) : null}

                    {/* Content area */}
                    {status === 'init' ? (
                        <div className="ar-init-message">
                            <i className="fe fe-bar-chart-2" style={{ fontSize: '48px', color: 'var(--color-text-muted)', marginBottom: 'var(--space-4)' }} />
                            <p>Select a time range and click <strong>Run Report</strong> to generate report.</p>
                            <button
                                className="dp-btn dp-btn--primary dp-btn--sm"
                                onClick={handleRunReport}
                                disabled={status === 'loading'}
                                style={{ marginTop: 'var(--space-3)' }}
                            >
                                <i className="fe fe-refresh-cw" style={{ marginRight: '4px' }} />
                                {status === 'loading' ? 'Running...' : 'Run Report'}
                            </button>
                        </div>
                    ) : null}

                    {status === 'loading' ? (
                        <div className="ar-loading">
                            <div className="ar-loading__spinner" />
                            <p>Loading report data...</p>
                        </div>
                    ) : null}

                    {status === 'ready' && overLimit ? (
                        <div className="ar-overlimit">
                            <i className="fe fe-alert-triangle" style={{ fontSize: '32px', color: 'var(--color-warning)' }} />
                            <h3>Report Too Large</h3>
                            <p>This report contains more than 5,000 events.</p>
                            {csvUrl ? (
                                <a href={csvUrl} className="dp-btn dp-btn--primary" target="_blank" rel="noopener noreferrer">
                                    <i className="fe fe-download" style={{ marginRight: '4px' }} />
                                    Download Full Report (CSV)
                                </a>
                            ) : null}
                        </div>
                    ) : null}

                    {status === 'ready' && !overLimit ? (
                        <div className="ar-detail-view">
                            {/* Controls row */}
                            <div className="ap-controls">
                                <div className="ap-controls__left">
                                    <div className="ap-search">
                                        <svg className="ap-icon-md ap-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                                        </svg>
                                        <input
                                            type="search"
                                            placeholder="Search events..."
                                            value={search}
                                            onChange={handleSearchChange}
                                            className="ap-search__input"
                                            aria-label="Search events"
                                        />
                                    </div>
                                </div>
                                <div className="ap-controls__right">
                                    <ColumnVisibilityToggle
                                        columns={columnDefs}
                                        visibleColumns={visibleColumns}
                                        onToggle={handleToggleColumn}
                                    />
                                    <button className="rp-btn rp-btn--secondary" onClick={handleExportCsv}>
                                        <i className="fe fe-download" style={{ marginRight: '4px' }} />
                                        CSV
                                    </button>
                                    <button className="rp-btn rp-btn--secondary" onClick={handleExportPdf}>
                                        <i className="fe fe-file-text" style={{ marginRight: '4px' }} />
                                        PDF
                                    </button>
                                </div>
                            </div>

                            <MaintenanceTable
                                data={paginatedData}
                                allData={reportData}
                                visibleColumns={visibleColumns}
                                sortColumn={sortColumn}
                                sortOrder={sortOrder}
                                onSort={handleSort}
                                filters={columnFilters}
                                onFilterChange={handleColumnFilterChange}
                                onClearFilters={handleClearColumnFilters}
                                isCloud={isCloud}
                                tzOffset={tzOffset}
                            />

                            {/* Pagination */}
                            <Pagination
                                page={safePage}
                                totalPages={totalPages}
                                total={totalFiltered}
                                perPage={perPage}
                                onPageChange={setPage}
                                onPerPageChange={(n) => { setPerPage(n); setPage(1); }}
                                entityLabel="events"
                                selectId="mr-per-page"
                            />
                            </div>
                    ) : null}

                    </div>{/* end ar-content */}

                    {/* Filter Drawer (4.0 / on-prem only — events accordion with Event Types only) */}
                    <ReportFilterDrawer
                        open={filterDrawerOpen}
                        onClose={() => setFilterDrawerOpen(false)}
                        onApply={handleApplyFilters}
                        onClear={handleClearReportFilters}
                        filterOptions={filterOptions}
                        currentFilters={reportFilters}
                        saving={status === 'loading'}
                        hideTimeRange
                        sections={MAINTENANCE_REPORT_SECTIONS}
                        hideResponseTime
                    />

                    {/* Schedule Report Drawer */}
                    <ScheduleReportDrawer
                        open={scheduleDrawerOpen}
                        onClose={() => setScheduleDrawerOpen(false)}
                        onSave={handleScheduleSave}
                        saving={false}
                        reportTypeId={REPORT_TYPE_ID.MAINTENANCE}
                        rangeId={rangeId}
                        rangeName={customRange ? 'Custom' : rangeName}
                        reportFilters={reportFilters}
                        filterOptions={filterOptions}
                        campusId={campusId}
                        frequencies={frequencies}
                        tzOffset={tzOffset}
                    />

                </div>
            </div>
        </>
    );
};

export default MaintenanceReportPage;
